export type ErrorKind = "client" | "api" | "prisma" | "unhandled";

export type ErrorEnvelope = {
  signature: string;
  kind: ErrorKind;
  status?: number;
  message: string;
  operation: string;
  route?: string;
  name?: string;
  commitSha?: string | null;
};

const UUID_RE =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const CPF_RE = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
const LONG_NUM_RE = /\b\d{5,}\b/g;

export function sanitizeErrorMessage(message: unknown, maxLen = 280): string {
  const text = String(message ?? "")
    .replace(EMAIL_RE, "[email]")
    .replace(CPF_RE, "[cpf]")
    .replace(UUID_RE, "[id]")
    .replace(LONG_NUM_RE, "[n]")
    .replace(/\s+/g, " ")
    .trim();
  return text.slice(0, maxLen);
}

export function normalizeErrorRoute(route: unknown): string {
  const raw = String(route ?? "").split("?")[0].trim();
  if (!raw) return "";
  return raw
    .replace(/\/\d+/g, "/[id]")
    .replace(/\/[0-9a-f-]{20,}/gi, "/[id]")
    .slice(0, 160);
}

export function buildErrorSignature(parts: {
  tipo?: string | null;
  status?: number | string | null;
  message?: string | null;
  operation?: string | null;
  route?: string | null;
}): string {
  const key = [
    parts.tipo ?? "Erro",
    parts.status ?? "",
    sanitizeErrorMessage(parts.message ?? "").toLowerCase(),
    String(parts.operation ?? "").trim().toLowerCase(),
    normalizeErrorRoute(parts.route),
  ].join("|");
  return hashSignature(key);
}

export function buildErrorEnvelope(input: {
  tipo?: string | null;
  kind: ErrorKind;
  status?: number | null;
  message?: string | null;
  operation?: string | null;
  route?: string | null;
  name?: string | null;
  commitSha?: string | null;
}): ErrorEnvelope {
  const message = sanitizeErrorMessage(input.message);
  const operation = String(input.operation ?? "").trim() || "unknown";
  const route = normalizeErrorRoute(input.route) || undefined;
  const status =
    typeof input.status === "number" && Number.isFinite(input.status)
      ? input.status
      : undefined;
  return {
    signature: buildErrorSignature({
      tipo: input.tipo,
      status,
      message,
      operation,
      route,
    }),
    kind: input.kind,
    ...(status != null ? { status } : {}),
    message,
    operation,
    ...(route ? { route } : {}),
    ...(input.name ? { name: input.name } : {}),
    ...(input.commitSha ? { commitSha: input.commitSha } : {}),
  };
}

function hashSignature(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) + hash + input.charCodeAt(i);
    hash |= 0;
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
